import HorizontalScrollRow from '../catalog/HorizontalScrollRow';

function getVisibleSections(bootstrap) {
  const sections = Array.isArray(bootstrap?.sections) ? bootstrap.sections : [];
  return sections.filter((section) => {
    if (!section || section.sectionType === 'VIDEO') return false;
    return Boolean(section.refData) || (Array.isArray(section.items) && section.items.length > 0);
  });
}

const DEFAULT_SKELETON_SECTIONS = [
  { id: 'fallback-1', title: '', displayMode: 'single', items: Array.from({ length: 5 }, (_, index) => ({ id: `fallback-1-${index}` })) },
  { id: 'fallback-2', title: '', displayMode: 'quad', items: Array.from({ length: 4 }, (_, index) => ({ id: `fallback-2-${index}` })) },
  { id: 'fallback-3', title: '', displayMode: 'single', items: Array.from({ length: 6 }, (_, index) => ({ id: `fallback-3-${index}` })) },
];

function getSectionRowItems(section) {
  const items = Array.isArray(section?.items) && section.items.length > 0
    ? section.items
    : (section?.refData ? [section.refData] : []);
  const canSeeAll = Boolean(section?.id) && Boolean(section?.refId) && Array.isArray(section?.items);
  const visibleCount = items.length > 0
    ? Math.max(4, Math.min(canSeeAll ? items.length : Math.max(items.length, 4), 6))
    : 5;
  const rowItems = Array.from({ length: visibleCount }, (_, index) => ({ kind: 'card', index }));
  if (canSeeAll && items.length > 10) {
    rowItems.push({ kind: 'see-all' });
  }
  return rowItems;
}

function getTitleWidth(sectionTitle) {
  const chars = String(sectionTitle || '').trim().length;
  if (!chars) return 160;
  return Math.max(96, Math.min(240, chars * 9));
}

function QuadSkeletonCard({ shimmerStyle }) {
  return (
    <div
      className="flex-shrink-0 w-40 h-40 rounded-home-24 overflow-hidden"
      style={{ background: '#151517' }}
    >
      <div className="grid h-full w-full grid-cols-2 grid-rows-2 gap-0.5 bg-bg-app">
        {[0, 1, 2, 3].map((tile) => (
          <div
            // eslint-disable-next-line react/no-array-index-key
            key={tile}
            className="skeleton-shimmer"
            style={shimmerStyle}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * HomeSkeleton — loading placeholder for Home that mirrors the last known
 * bootstrap layout, so the loaded content lands into the same slots.
 */
export default function HomeSkeleton({ bootstrap = null }) {
  const sections = getVisibleSections(bootstrap);
  const hasHero = Array.isArray(bootstrap?.heroBanners) && bootstrap.heroBanners.length > 0;
  const fallbackSections = sections.length > 0
    ? sections
    : DEFAULT_SKELETON_SECTIONS;
  const shimmerStyle = {
    '--skeleton-base': '#151517',
  };
  const titleShimmerStyle = {
    '--skeleton-base': '#171719',
  };

  return (
    <div
      data-testid="home-skeleton"
      className="min-h-screen bg-bg-app text-white"
      style={{ paddingTop: 'calc(var(--safe-top, 0px) + 30px)' }}
    >
      {hasHero && (
        <div className="px-0">
          <div
            data-role="skeleton-hero"
            className="w-full h-hero-h skeleton-shimmer"
            style={shimmerStyle}
          />
        </div>
      )}
      <div className="flex flex-col gap-gap-section px-body-x pt-gap-section pb-body-pb">
        {fallbackSections.map((section, index) => {
          const rowItems = getSectionRowItems(section);
          const displayMode = section?.displayMode || 'single';
          const canSeeAll = Boolean(section?.id) && Boolean(section?.refId) && Array.isArray(section?.items);
          return (
          <div
            key={section?.id || index}
            data-role="skeleton-section"
            className="flex flex-col gap-gap-title-cards"
          >
            <div className="flex items-center justify-between gap-3">
              <div
                className="h-5 rounded-md skeleton-shimmer"
                style={{ ...titleShimmerStyle, width: `${getTitleWidth(section?.title)}px` }}
              />
              {canSeeAll && (
                <div
                  className="h-5 w-[74px] rounded-md skeleton-shimmer shrink-0"
                  style={titleShimmerStyle}
                />
              )}
            </div>
            <HorizontalScrollRow
              className="flex flex-row items-stretch gap-gap-cards overflow-x-auto scrollbar-hide -mx-4 px-4"
              items={rowItems}
              itemWidth={(item) => (item?.kind === 'see-all' ? 80 : 160)}
              initialItemCount={4}
              overscan={0}
              placeholderHeight={160}
              deferUntilVisible={false}
              renderItem={(item, itemIndex) => {
                if (item.kind === 'see-all') {
                  return (
                    <div
                      key={`see-all-${section?.id || index}`}
                      className="h-40 w-20 rounded-home-24 skeleton-shimmer"
                      style={shimmerStyle}
                    />
                  );
                }

                if (displayMode === 'quad') {
                  return (
                    <QuadSkeletonCard
                      key={`${section?.id || index}-quad-${itemIndex}`}
                      shimmerStyle={shimmerStyle}
                    />
                  );
                }

                return (
                  <div
                    key={`${section?.id || index}-card-${itemIndex}`}
                    className="h-40 w-40 rounded-home-24 skeleton-shimmer"
                    style={shimmerStyle}
                  />
                );
              }}
            >
              {null}
            </HorizontalScrollRow>
          </div>
          );
        })}
      </div>
    </div>
  );
}
